import React from 'react';

import { useSelector } from '@datagrid/state';
import { Link } from 'react-router-dom';

import type { BackendTypes } from '@tf/api';
import {
	Box,
	Button,
	Center,
	createStyles,
	Icon,
	TFCard,
	TFDataTable,
	TFEmptyStateMessage,
	TFText,
	Tooltip,
	UnstyledButton,
	useDebouncedValue,
	useLocalStorage,
	usePersistentPagination,
} from '@tf/ui';
import { D, fmt, S } from '@tf/utils';

import { useOverviewAccounts } from '@/core/api/overview';
import { appStore } from '@/core/stores';
import { AccountPriorityWidget } from '@/components/accounts/AccountTable/AccountPriorityWidget';
import { AccountStatus } from '@/components/accounts/AccountTable/AccountStatus';
import {
	AssignedAccountManagersWidget,
	HiddenAccountsNotice,
	ReviewState,
	TFBadge,
	TFBadgeColors,
} from '@/components/shared';
import { getProfilesFilters } from '@/components/utils';

const riskLevelColors: Record<string, TFBadgeColors> = {
	VERY_HIGH: TFBadgeColors.RED,
	HIGH: TFBadgeColors.ORANGE,
	MEDIUM: TFBadgeColors.YELLOW,
	LOW: TFBadgeColors.GREEN,
};

const useStyles = createStyles(({ colors }) => ({
	groupName: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
	profileName: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
	profileNameText: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: 300,
	},
}));

const sortingNamesMap: Record<string, string> = {
	name: 'ACCOUNT_NAME',
	kind: 'ACCOUNT_TYPE',
	'values.accountStatus': 'ACCOUNT_STATUS',
	verification: 'VERIFICATION_STATUS',
	importance: 'PRIORITY',
	'values.nextKycReviewDate': 'KYC_VIEW',
	statusChangedAt: 'ACCOUNT_STATUS_CHANGED_AT',
};

const filtersNamesMap: Record<string, string> = {
	kind: 'accountType',
	'values.accountStatus': 'accountStatus',
	verification: 'verificationStatus',
	importance: 'priority',
	assignedTo: 'assignedTo',
};

interface Props {
	kinds: string[];
	label: string;
}

export const ProfileGroupsTable = ({ kinds, label }: Props) => {
	const { classes } = useStyles();
	const kindsOptions = useSelector(() => appStore.defs.options['AccountKind'].get());

	const getStorageKey = (suffix: string) => `${label.toLowerCase()}-${suffix}`;

	const [pagination, setPagination] = usePersistentPagination(getStorageKey('table'), {
		pageSize: 10,
		pageIndex: 0,
	});

	const [searchValue, setSearchValue] = useLocalStorage<string>({
		defaultValue: '',
		key: getStorageKey('table-search'),
		getInitialValueInEffect: false,
	});

	const [debouncedSearch] = useDebouncedValue(searchValue, 500);

	const [filtersState, setFiltersState] = useLocalStorage<
		{
			id: string;
			value: unknown;
		}[]
	>({
		defaultValue: [],
		key: getStorageKey('table-filters'),
		getInitialValueInEffect: false,
	});

	const [sortingState, setSortingState] = useLocalStorage<
		{
			desc: boolean;
			id: string;
		}[]
	>({
		defaultValue: [{ desc: true, id: 'name' }],
		key: getStorageKey('table-sorting'),
		getInitialValueInEffect: false,
	});

	const sortingParams =
		sortingState.length > 0
			? {
					sortBy: sortingNamesMap[sortingState[0].id] as BackendTypes.OverviewSortBy | undefined,
					sortOrder:
						sortingState[0].id === 'statusChangedAt'
							? sortingState[0].desc
								? 'ASC'
								: 'DESC'
							: sortingState[0].desc
							? 'DESC'
							: ('ASC' as BackendTypes.OverviewSortOrder),
			  }
			: undefined;

	const filteringParams = filtersState.reduce<Record<string, any>>((acc, curr) => {
		acc[filtersNamesMap[curr.id]] = curr.value;
		return acc;
	}, {});

	const hasArchivedStatusMessage = Boolean(
		!filtersState.find((f) => f.value === 'ARCHIVED') && !!debouncedSearch
	);

	const { data, isPending, isError } = useOverviewAccounts(
		pagination.pageIndex + 1,
		pagination.pageSize,
		{
			...filteringParams,
			...sortingParams,
			accountTypes: kinds,
			query: searchValue === '' ? searchValue : debouncedSearch,
		}
	);

	const accounts = data?.accounts;
	const listUsers = data?.listUsers ?? [];

	const refreshPageButton = (
		<Button
			variant="light"
			size="md"
			leftSection={<Icon.IconRefresh size={18} />}
			onClick={() => window.location.reload()}
		>
			Refresh the page
		</Button>
	);

	const errorMessage = {
		icon: <Icon.IconUnlink />,
		title: 'Something went wrong',
		description: 'Try to refresh the page or come back later',
		primaryAction: refreshPageButton,
	};

	if (isError) {
		return (
			<TFCard>
				<Center h={300}>
					<TFEmptyStateMessage {...errorMessage} />
				</Center>
			</TFCard>
		);
	}

	return (
		<>
			<TFDataTable<BackendTypes.OverviewAccount>
				noFilteredDataMessage={{
					icon: <Icon.IconUsers />,
					title: `No ${label.toLowerCase()} found`,
				}}
				noDataMessage={{
					icon: <Icon.IconUsers />,
					title: `No ${label.toLowerCase()} found`,
				}}
				name={`${label.toLowerCase()}-list`}
				isLoading={isPending}
				serverSorting={{
					onSortingChange: setSortingState,
					state: sortingState,
				}}
				serverFiltering={{
					onFiltersChange: setFiltersState,
					state: filtersState,
				}}
				serverPagination={
					accounts
						? {
								...pagination,
								rowCount: accounts.count,
								onPaginationChange: setPagination,
						  }
						: undefined
				}
				serverSearch={{
					state: searchValue,
					onSearchChange: setSearchValue,
				}}
				searchPlaceholder={`Search by ${label.toLowerCase()} name`}
				data={accounts?.items ?? []}
				defs={[
					{
						header: 'Name',
						accessorKey: 'name',
						size: 200,
						enableSorting: true,
						Cell: ({ cell }) => {
							const { nodeId, name } = cell.row.original;
							return (
								<UnstyledButton
									component={Link}
									to={`/accounts/${nodeId}`}
									className={classes.profileName}
								>
									<Tooltip label={name || 'N/A'} position="top">
										<TFText inherit className={classes.profileNameText}>
											{name || 'N/A'}
										</TFText>
									</Tooltip>
								</UnstyledButton>
							);
						},
					},
					{
						header: 'Type',
						accessorKey: 'kind',
						enableSorting: false,
						Cell: ({ cell }) => {
							const nextKind = kindsOptions?.find((a: { value: string }) => a.value === cell.getValue());
							return (
								<TFText inherit lineClamp={1}>
									{nextKind?.label || 'Not defined'}
								</TFText>
							);
						},
					},
					{
						header: 'Status',
						id: 'values.accountStatus',
						accessorKey: 'values.accountStatus',
						sortUndefined: 1,
						Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
							return <AccountStatus accountStatus={row.original.values?.accountStatus} />;
						},
					},
					{
						header: 'Time in status',
						id: 'statusChangedAt',
						accessorKey: 'statusChangedAt',
						Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
							const changedAt = row.original.statusChangedAt;
							return (
								<Tooltip
									label={`Since ${fmt.toDate(changedAt, { preset: 'full_date_with_time' })}`}
									position="top"
								>
									<div>{D.calculateTillNow(changedAt)}</div>
								</Tooltip>
							);
						},
					},
					{
						header: 'Verification status',
						accessorKey: 'verification',
						Cell: ({ cell }) => <ReviewState account={cell.row.original} />,
					},
					{
						header: 'Assigned managers',
						accessorKey: 'assignedManagers',
						enableSorting: false,
						Cell: (data: { row: { original: { nodeId: number; assignedManagers: string[] } } }) => (
							<AssignedAccountManagersWidget
								accountId={data.row.original.nodeId}
								selectedIds={data.row.original.assignedManagers}
								managers={listUsers ?? []}
							/>
						),
					},
					{
						header: 'Priority',
						accessorKey: 'importance',
						Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => (
							<AccountPriorityWidget accountId={row.original.nodeId} value={row.original.importance} />
						),
					},
					{
						header: 'Risk profile',
						accessorKey: 'values.riskLevel',
						enableSorting: false,
						Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
							const riskLevel = row.original.values.riskLevel;
							return (
								<TFBadge
									withBackground={false}
									text={riskLevel ? S.prettify(riskLevel) : 'Not set'}
									color={riskLevel ? riskLevelColors[riskLevel] : TFBadgeColors.DIMMED}
								/>
							);
						},
					},
					{
						header: 'Next KYC review',
						accessorKey: 'values.nextKycReviewDate',
						Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
							const date: string | undefined = row.original.values.nextKycReviewDate;
							const status = row.original.values.accountStatus;
							if (!date || status === 'REJECTED' || status === 'CLOSED') {
								return (
									<TFText inherit c="dimmed">
										Not defined
									</TFText>
								);
							}
							const daysOverdue = D.getOverdueDaysCount(date);
							return (
								<TFText color={daysOverdue ? 'red' : 'initial'}>
									{daysOverdue > 0
										? `Overdue ${daysOverdue} ${daysOverdue > 1 ? 'days' : 'day'}`
										: fmt.toDate(date, { preset: 'full_date' })}
								</TFText>
							);
						},
					},
				]}
				availableFilters={getProfilesFilters(listUsers, kindsOptions, kinds)}
			/>
			<Box mt="12px">
				{hasArchivedStatusMessage && (
					<HiddenAccountsNotice
						textSize="13px"
						withIcon
						onClick={() => setFiltersState([{ id: 'values.accountStatus', value: 'ARCHIVED' }])}
					/>
				)}
			</Box>
		</>
	);
};
